import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem, Card, CardBody, CardHeader, Table, Badge } from "reactstrap";
import cogoToast from "cogo-toast";
import { ENDPOINTS, SUCCESS_RESULT } from "../../Constants";
import { genericGet } from "../../services/Api";

const BeneficiaryClockings = () => {
  const { cycle_id: cycleIdFromUrl } = useParams();
  const location = useLocation();
  const cycleIdFromState = location?.state?.cycle_id || location?.state?.cycle || "";
  const cycleNameFromState = location?.state?.cycle_name || "";
  const cycleIdFromQuery = new URLSearchParams(location.search).get("cycle_id") || "";
  const cycleId = cycleIdFromUrl || cycleIdFromState || cycleIdFromQuery;
  const cycleDisplayName = cycleNameFromState || cycleId;

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!cycleId) return;
    setIsLoading(true);
    genericGet(`${ENDPOINTS.LIST_CLOCKINS}?cycle_id=${cycleId}`, {})
      .then((res) => {
        setIsLoading(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          setRows(Array.isArray(res?.data?.data) ? res.data.data : []);
          return;
        }
        setRows([]);
        cogoToast.error(res?.data?.message || "Failed to load beneficiary clockings", { hideAfter: 4 });
      })
      .catch(() => {
        setIsLoading(false);
        setRows([]);
        cogoToast.error("Failed to load beneficiary clockings", { hideAfter: 4 });
      });
  }, [cycleId]);

  const dateKeys = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const raw = String(row?.clockin_date_time || "");
      const day = raw.split(" ")[0];
      if (day) set.add(day);
    });
    return Array.from(set).sort((a, b) => new Date(a) - new Date(b));
  }, [rows]);

  const beneficiaries = useMemo(() => {
    const map = {};
    rows.forEach((row) => {
      const b = row?.beneficiary || {};
      const beneficiaryId = b?.id || row?.beneficiary_id;
      if (!beneficiaryId) return;
      if (!map[beneficiaryId]) {
        map[beneficiaryId] = {
          id: beneficiaryId,
          name: b?.name || "-",
          telephone: b?.telephone || "-",
          attendedDates: {},
        };
      }
      const raw = String(row?.clockin_date_time || "");
      const day = raw.split(" ")[0];
      if (day) map[beneficiaryId].attendedDates[day] = true;
    });
    return Object.values(map);
  }, [rows]);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      cogoToast.error("Unable to open print window. Please allow popups.", { hideAfter: 4 });
      return;
    }

    const headerCells = dateKeys.map((day) => `<th>${day}</th>`).join("");
    const bodyRows = beneficiaries
      .map((b, idx) => {
        const attendanceCells = dateKeys
          .map((day) => `<td class="center">${b.attendedDates[day] ? "✓" : ""}</td>`)
          .join("");
        return `<tr>
          <td>${idx + 1}</td>
          <td>${b.name || "-"}</td>
          <td>${b.telephone || "-"}</td>
          ${attendanceCells}
        </tr>`;
      })
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>Beneficiary Clockings - ${cycleDisplayName}</title>
          <style>
            @page { size: landscape; margin: 12mm; }
            body { font-family: Arial, sans-serif; color: #222; }
            .header { text-align: center; margin-bottom: 12px; }
            .logo { height: 48px; margin-bottom: 6px; }
            .title { font-size: 25px; font-weight: 700; margin: 0; }
            .subtitle { font-size: 19px; margin: 6px 0 0; color: #444; }
            .meta { font-size: 12px; color: #666; margin-top: 4px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { border: 1px solid #cfd6de; padding: 6px; }
            th { background: #eef4ff; }
            .center { text-align: center; font-weight: 700; }
          </style>
        </head>
        <body>
          <div class="header">
            <img class="logo" src="https://irw-ebms-ear.co.ke/images/irs_logo.png" alt="IR Somalia" />
            <p class="title">Attendance Clockings Report</p>
            <p class="subtitle"> ${cycleDisplayName}</p>
            <p class="meta">Generated: ${new Date().toLocaleString()}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Attendee Name</th>
                <th>Telephone</th>
                ${headerCells}
              </tr>
            </thead>
            <tbody>
              ${
                bodyRows ||
                `<tr><td colspan="${3 + dateKeys.length}" class="center">No clockings found for this cycle.</td></tr>`
              }
            </tbody>
          </table>
          <p class="meta" style="text-align:center; margin-top: 10px; font-style: italic;">
            This is an electronically generated report from EBMS and requires no signature
          </p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  if (!cycleId) {
    return (
      <div style={{ padding: "20px" }}>
        <div className="alert alert-warning mb-0">Missing cycle id. Open this page with a valid cycle.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-card { box-shadow: none !important; border: 1px solid #ddd !important; }
          .print-wrap { background: #fff !important; padding: 0 !important; }
          table { font-size: 11px; }
          th, td { padding: 4px 6px !important; }
        }
      `}</style>

      <h2 style={{ marginBottom: 10 }}>Beneficiary Clockings</h2>
      <Breadcrumb className="ms-1 mb-3 no-print">
        <BreadcrumbItem>
          <Link to="/dashboard">Dashboard</Link>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <Link to="/projects/list">Projects</Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Beneficiary Clockings</BreadcrumbItem>
      </Breadcrumb>

      <Card className="border-0 shadow-sm print-card">
        <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2 no-print">
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Attendance Matrix</span>
            <Badge color="info" pill>
              Beneficiaries: {beneficiaries.length}
            </Badge>
            <Badge color="secondary" pill>
              Dates: {dateKeys.length}
            </Badge>
          </div>
          <button className="btn btn-sm btn-outline-primary" onClick={handlePrint}>
            Print
          </button>
        </CardHeader>
        <CardBody style={{ padding: 0 }} className="print-wrap">
          {isLoading ? (
            <div className="text-center text-muted py-4">Loading beneficiary clockings...</div>
          ) : (
            <>
              <div className="px-3 pt-3 pb-2" style={{ textAlign: "center" }}>
                <img src="/images/irs_logo.png" alt="IR Somalia" style={{ height: 50, objectFit: "contain" }} />
                <div style={{ fontWeight: 700, fontSize: "1.1rem", marginTop: 6 }}>Islamic Relief Somalia</div>
                <div style={{ color: "#6c757d", fontSize: "0.9rem" }}>Beneficiary Attendance Clockings</div>
                <div style={{ color: "#6c757d", fontSize: "0.82rem" }}>Generated: {new Date().toLocaleString()}</div>
              </div>

              <div className="px-3 pb-2">
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: "1.25rem",
                    padding: "8px 10px",
                    borderRadius: 6,
                    background: "#eef4ff",
                    color: "#1f3b64",
                  }}
                >
                  Cycle Name: {cycleDisplayName}
                </div>
              </div>

              <div className="table-responsive">
              <Table bordered hover className="mb-0">
                <thead>
                  <tr>
                    <th style={{ minWidth: 60 }}>#</th>
                    <th style={{ minWidth: 240 }}>Attendee Name</th>
                    <th style={{ minWidth: 170 }}>Telephone</th>
                    {dateKeys.map((day) => (
                      <th key={day} style={{ minWidth: 120, textAlign: "center", whiteSpace: "nowrap" }}>
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {beneficiaries.map((b, idx) => (
                    <tr key={b.id}>
                      <td>{idx + 1}</td>
                      <td>{b.name}</td>
                      <td>{b.telephone}</td>
                      {dateKeys.map((day) => (
                        <td key={`${b.id}-${day}`} style={{ textAlign: "center", fontSize: "1.1rem" }}>
                          {b.attendedDates[day] ? "✓" : ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {beneficiaries.length === 0 && (
                    <tr>
                      <td colSpan={3 + dateKeys.length} className="text-center text-muted py-3">
                        No clockings found for this cycle.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            </>
          )}
        </CardBody>
      </Card>

      <div
        style={{
          marginTop: 12,
          textAlign: "center",
          fontSize: "0.85rem",
          color: "#6c757d",
          fontStyle: "italic",
        }}
      >
        This is an electronically generated report from EBMS and requires no signature
      </div>
    </div>
  );
};

export default BeneficiaryClockings;
